import React, { useState, useCallback, useEffect } from "react";
import {
  Card,
  Text,
  Group,
  Title,
  ScrollArea,
  Button,
  Badge,
  Modal,
  Paper,
  TextInput,
  LoadingOverlay,
  SegmentedControl,
  Box,
  Stack,
  createStyles,
  useMantineTheme,
} from "@mantine/core";

import { MapPin, PhoneCall, CalendarTime, Pencil, Search } from "tabler-icons-react";
import { customLoader } from "../../theme/loader";
import { alertNotify, checkNotify } from "../../Notify/notifyMe";
import { baseUrl } from "../../config/endPoints";
import authAxios from "../../keycloak/authAxios";
import { useAuthImage } from "../../keycloak/useAuthImage";

const brandColor = "rgb(42, 155, 73)";

const useStyles = createStyles((theme) => ({
  wrapper: { height: "100%", display: "flex", flexDirection: "column" },
  emptyResults: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : "#868e96",
    textAlign: "center",
    padding: 24,
    minHeight: 0,
  },
  countBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(42,155,73,0.08)",
    color: brandColor,
    border: `1px solid rgba(42,155,73,0.15)`,
    borderRadius: theme.radius.md,
    padding: "6px 12px",
    fontSize: 14,
    fontWeight: 500,
  },
  card: {
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : "#e9ecef"}`,
    borderRadius: theme.radius.lg,
    padding: theme.spacing.lg,
    transition: "border-color 0.2s ease, transform 0.2s ease",
    boxShadow: "none",
    "&:hover": { borderColor: brandColor, transform: "translateY(-2px)" },
  },
  imageWrap: {
    width: 120,
    height: 120,
    borderRadius: theme.radius.md,
    overflow: "hidden",
    flexShrink: 0,
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : "#f8f9fa",
  },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  placeholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : "#adb5bd",
    fontSize: 13,
    textAlign: "center",
  },
  name: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.colorScheme === "dark" ? theme.white : theme.colors.gray[9],
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : "#868e96",
    fontSize: 14,
  },
  metaIcon: { color: brandColor },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  editButton: {
    color: brandColor,
    border: `1px solid rgba(42,155,73,0.2)`,
    backgroundColor: "transparent",
    borderRadius: theme.radius.md,
    fontWeight: 500,
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "rgba(42,155,73,0.05)" },
  },
  saveButton: {
    backgroundColor: brandColor,
    color: theme.white,
    borderRadius: theme.radius.md,
    fontWeight: 500,
    border: "none",
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "rgb(35,135,60)" },
  },
  modal: {
    ".mantine-Modal-modal": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
      borderRadius: theme.radius.lg,
      boxShadow: "none",
    },
    ".mantine-Modal-close": {
      color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.gray[7],
    },
  },
  modalPaper: {
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : "#e9ecef"}`,
    borderRadius: theme.radius.lg,
    padding: theme.spacing.xl,
  },
  modalInput: {
    ".mantine-TextInput-input": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
      border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : "#e9ecef"}`,
      borderRadius: theme.radius.md,
      transition: "border-color 0.2s ease",
      boxShadow: "none",
      "&:focus": { borderColor: brandColor, boxShadow: "none" },
    },
    ".mantine-TextInput-label": { fontWeight: 500, marginBottom: 6 },
  },
  segmented: {
    ".mantine-SegmentedControl-label": { fontWeight: 500 },
  },
}));

class PersonState {
  constructor() {
    this.states = [
      { title: "غير محدد", value: "UNKOWN" },
      { title: "مفقود", value: "LOST" },
      { title: "موجود", value: "FOUND" },
    ];
  }

  getTitles = () => this.states.flatMap((itm) => itm.title);
  getValues = () => this.states.flatMap((itm) => itm.value);

  parseTitle = (title) => {
    const fil = this.states.filter((item) => item.title === title);
    if (fil && fil.length) return fil[0].value;
    return title;
  };

  parseValue = (value) => {
    const fil = this.states.filter((item) => item.value === value);
    if (fil && fil.length) return fil[0].title;
    return value;
  };
}

export const ResultCrad = ({ foundPersonResult, toggle }) => {
  const [open, setOpen] = useState(false);
  const [currentProfileInfo, setCurrentProfileInfo] = useState(null);

  return (
    <>
      {!toggle ? (
        <>
          {currentProfileInfo && (
            <EditModal
              open={open}
              foundPersonResult={currentProfileInfo}
              setOpen={setOpen}
            />
          )}
          <Cards
            setCurrentProfileInfo={setCurrentProfileInfo}
            foundPersonResult={foundPersonResult}
            baseUrl={baseUrl}
            setOpen={setOpen}
          />
        </>
      ) : null}
    </>
  );
};

function EditModal({ open, setOpen, foundPersonResult }) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const personState = new PersonState();

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [Flag, setFlag] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(foundPersonResult.full_name);
    setPhoneNumber(foundPersonResult.phone_number);
    setAddress(foundPersonResult.address);
    setFlag(foundPersonResult.flag);
  }, [foundPersonResult]);

  const handleUpdateUser = useCallback(() => {
    setLoading(true);

    const payload = {
      full_name: name,
      phone_number: phoneNumber,
      address: address,
      flag: Flag,
    };

    authAxios
      .patch(`arshad/${foundPersonResult.id}`, payload)
      .then((res) => {
        if (res.data.result) {
          setLoading(false);
          setOpen(false);
          checkNotify("تم تعديل معلومات الشخص");
        } else {
          setLoading(false);
          alertNotify("لم يتم تعديل معلومات الشخص");
        }
      })
      .catch(() => {
        setLoading(false);
        alertNotify("لم يتم تعديل معلومات الشخص");
      });
  }, [Flag, name, phoneNumber, address, foundPersonResult]);

  return (
    <>
      <Modal
        centered
        opened={open}
        onClose={() => setOpen(false)}
        title={<Text weight={600} size="lg">يمكنك تعديل حقول المعلومات التالية</Text>}
        size="lg"
        radius="lg"
        overlayOpacity={0.2}
        overlayBlur={0}
        overlayColor={theme.colorScheme === "dark" ? theme.colors.dark[9] : theme.colors.gray[2]}
        className={classes.modal}
      >
        <Paper className={classes.modalPaper}>
          <Stack spacing="md">
            <TextInput
              label="الاسم"
              size="md"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={classes.modalInput}
            />
            <TextInput
              label="رقم الهاتف"
              size="md"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={classes.modalInput}
            />
            <TextInput
              label="العنوان"
              size="md"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={classes.modalInput}
            />
            <Box>
              <Text weight={500} size="sm" mb={8}>حالة الشخص</Text>
              <SegmentedControl
                value={Flag}
                onChange={setFlag}
                data={personState.states.map((s) => ({ label: s.title, value: s.value }))}
                color="green"
                radius="md"
                className={classes.segmented}
                fullWidth
              />
            </Box>
            <Button
              fullWidth
              size="md"
              onClick={handleUpdateUser}
              className={classes.saveButton}
            >
              حفظ التعديلات
            </Button>
          </Stack>
        </Paper>
        {loading && (
          <LoadingOverlay
            visible={loading}
            loader={customLoader}
            overlayOpacity={0.2}
            overlayBlur={0}
            overlayColor={theme.colorScheme === "dark" ? theme.colors.dark[9] : theme.colors.gray[2]}
          />
        )}
      </Modal>
    </>
  );
}

function Cards({ foundPersonResult, baseUrl, setOpen, setCurrentProfileInfo }) {
  const { classes } = useStyles();
  const personState = new PersonState();
  const [visibleResults, setVisibleResults] = useState(10);
  const [loading, setLoading] = useState(false);

  const loadMore = () => {
    setLoading(true);
    setTimeout(() => {
      setVisibleResults((prev) => prev + 10);
      setLoading(false);
    }, 500);
  };

  const hasMore = visibleResults < foundPersonResult.length;

  return (
    <div className={classes.wrapper}>
      <Group position="apart" align="center" mb="md">
        <div className={classes.countBadge}>عدد الاشخاص: {foundPersonResult.length}</div>
      </Group>

      {foundPersonResult.length === 0 ? (
        <Box className={classes.emptyResults}>
          <Search size={48} color={brandColor} opacity={0.4} />
          <Text size="lg" weight={500} mt={16}>لا توجد نتائج</Text>
          <Text size="sm" color="dimmed" mt={4}>ابدأ بالبحث بالاسم أو التقاط صورة</Text>
        </Box>
      ) : (
        <ScrollArea sx={{ flex: 1, minHeight: 0 }} scrollbarSize={6} type="hover">
          <Stack spacing="md">
            {foundPersonResult.slice(0, visibleResults).map((user) => (
              <PersonCard
                key={user.id}
                user={user}
                classes={classes}
                personState={personState}
                setCurrentProfileInfo={setCurrentProfileInfo}
                setOpen={setOpen}
              />
            ))}
          </Stack>

          <Box py={20} sx={{ display: "flex", justifyContent: "center" }}>
            {loading ? (
              <Box sx={{ textAlign: "center" }}>
                {customLoader}
                <Text size="sm" color="dimmed" mt={10}>جاري تحميل المزيد...</Text>
              </Box>
            ) : hasMore ? (
              <Button
                onClick={loadMore}
                size="sm"
                radius="md"
                className={classes.editButton}
              >
                عرض المزيد
              </Button>
            ) : foundPersonResult.length > 10 ? (
              <Text size="sm" color="dimmed">تم عرض جميع النتائج</Text>
            ) : null}
          </Box>
        </ScrollArea>
      )}
    </div>
  );
}

function PersonCard({ user, classes, personState, setCurrentProfileInfo, setOpen }) {
  const photoId = user.photos && user.photos[0] ? user.photos[0] : null;
  const { imageSrc, loading: imgLoading } = useAuthImage(photoId);

  return (
    <Card className={classes.card}>
      <Group noWrap align="flex-start" spacing="md">
        <div className={classes.imageWrap}>
          {imgLoading ? (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {customLoader}
            </div>
          ) : imageSrc ? (
            <a
              href={imageSrc}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <img src={imageSrc} alt={user.full_name} className={classes.image} />
            </a>
          ) : (
            <div className={classes.placeholder}>لا توجد صورة</div>
          )}
        </div>

        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group position="apart" align="center" noWrap spacing="sm" mb={8}>
            <Title order={3} className={classes.name}>{user.full_name}</Title>
            {(() => {
              const status = personState.states.find((s) => s.value === user.flag);
              return status ? (
                <Badge
                  size="md"
                  radius="md"
                  variant="outline"
                  color={status.value === "FOUND" ? "blue" : "red"}
                  sx={{ textTransform: "none" }}
                >
                  {status.title}
                </Badge>
              ) : null;
            })()}
          </Group>

          <Stack spacing={8}>
            <Group spacing={8} noWrap>
              <MapPin size={18} className={classes.metaIcon} />
              <Text
                className={classes.meta}
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {user.address}
              </Text>
            </Group>
            <Group spacing={8} noWrap>
              <PhoneCall size={18} className={classes.metaIcon} />
              <Text className={classes.meta}>{user.phone_number}</Text>
            </Group>
            <Group spacing={8} noWrap>
              <CalendarTime size={18} className={classes.metaIcon} />
              <Text className={classes.meta}>
                العمر: {user.age === "unknown" ? "غير محدد" : user.age}
              </Text>
            </Group>
          </Stack>

          <div className={classes.actions}>
            {user.similarity && (
              <Badge
                size="md"
                radius="md"
                variant="outline"
                color="green"
                sx={{ textTransform: "none" }}
              >
                التطابق: {Math.round(user.similarity * 1000) / 10}%
              </Badge>
            )}
            <Button
              className={classes.editButton}
              size="xs"
              leftIcon={<Pencil size={14} />}
              onClick={() => {
                setCurrentProfileInfo(user);
                setOpen(true);
              }}
            >
              تعديل
            </Button>
          </div>
        </Box>
      </Group>
    </Card>
  );
}
